// Copyright 2026, Auxilio Technologies LLC

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../store/themeStore';
import { useAuthStore } from '../store/authStore';
import { useShopStore } from '../store/shopStore';
import { shopsAPI } from '../services/api';
import { getRoleInfo, RoleType, isAdminRole } from '../config/rolesConfig';

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, setDarkMode } = useThemeStore();
  const { user } = useAuthStore();
  const { shops, activeShopId, setShops, setActiveShop } = useShopStore();
  const isAdmin = isAdminRole((user?.role || 'viewer') as RoleType);
  const current = shops.find((s) => s.id === activeShopId);

  const [shopName, setShopName] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [shopPhone, setShopPhone] = useState('');
  const [shopNotice, setShopNotice] = useState('');
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');

  // Seed the edit fields from the active shop the first time it's available.
  React.useEffect(() => {
    if (current) {
      setShopName(current.name || '');
      setShopAddress(current.address || '');
      setShopPhone(current.phone || '');
    }
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshShops = async (selectId?: string) => {
    const r = await shopsAPI.list();
    setShops(r.data.shops || []);
    if (selectId) setActiveShop(selectId);
  };

  const saveShop = async () => {
    if (!activeShopId) return;
    setShopNotice('');
    try {
      await shopsAPI.update(activeShopId, { name: shopName, address: shopAddress, phone: shopPhone });
      await refreshShops();
      setShopNotice(t('shop.saved'));
    } catch (e: any) {
      setShopNotice(e.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  const addShop = async (e: React.FormEvent) => {
    e.preventDefault();
    setShopNotice('');
    try {
      const r = await shopsAPI.create({ name: newName, address: newAddress });
      setNewName('');
      setNewAddress('');
      await refreshShops(r.data.shop?.id);
      setShopNotice(t('shop.created'));
    } catch (e: any) {
      setShopNotice(e.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
  ];

  const roleInfo = getRoleInfo((user?.role || 'viewer') as RoleType);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">{t('settings.subtitle')}</p>
      </div>

      {/* Shop (source address + multi-shop management) */}
      {current && (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('shop.title')}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('shop.sub')}</p>
          </div>
          <div className="space-y-4 p-6">
            {shopNotice && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                {shopNotice}
              </div>
            )}
            {isAdmin ? (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('shop.name')}</label>
                  <input value={shopName} onChange={(e) => setShopName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('shop.address')}</label>
                  <input value={shopAddress} onChange={(e) => setShopAddress(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('shop.phone')}</label>
                  <input value={shopPhone} onChange={(e) => setShopPhone(e.target.value)}
                    className="w-full max-w-xs rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                </div>
                <button onClick={saveShop} className="rounded-lg bg-rose-600 px-5 py-2 text-sm font-medium text-white hover:bg-rose-700">
                  {t('shop.save')}
                </button>

                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">{t('shop.addTitle')}</h3>
                  <form onSubmit={addShop} className="flex flex-wrap items-end gap-2">
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} required placeholder={t('shop.newName')}
                      className="flex-1 min-w-[160px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    <input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder={t('shop.newAddress')}
                      className="flex-1 min-w-[220px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    <button type="submit" className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">
                      {t('shop.add')}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">{t('shop.name')}</label>
                  <p className="font-medium text-gray-900 dark:text-white">{current.name}</p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">{t('shop.address')}</label>
                  <p className="font-medium text-gray-900 dark:text-white">{current.address || '—'}</p>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500">{t('shop.readonly')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Appearance */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.appearance')}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('settings.appearanceSub')}</p>
        </div>
        <div className="p-6">
          <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.theme')}</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setDarkMode(false)}
              className={`flex flex-col items-center rounded-xl border-2 p-4 transition ${
                !isDarkMode ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20' : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className="mb-3 h-16 w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="h-4 bg-rose-600" />
                <div className="p-2"><div className="h-2 w-3/4 rounded bg-gray-200" /></div>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{t('settings.light')}</span>
            </button>
            <button
              onClick={() => setDarkMode(true)}
              className={`flex flex-col items-center rounded-xl border-2 p-4 transition ${
                isDarkMode ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20' : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className="mb-3 h-16 w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-900">
                <div className="h-4 bg-rose-600" />
                <div className="p-2"><div className="h-2 w-3/4 rounded bg-gray-700" /></div>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{t('settings.dark')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Language */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.language')}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('settings.languageSub')}</p>
        </div>
        <div className="p-6">
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.displayLanguage')}</label>
          <select
            value={i18n.language?.split('-')[0] || 'en'}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="w-full max-w-xs rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Account */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.account')}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('settings.accountSub')}</p>
        </div>
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">{t('auth.email')}</label>
            <p className="font-medium text-gray-900 dark:text-white">{user?.email || '—'}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">{t('auth.username')}</label>
            <p className="font-medium text-gray-900 dark:text-white">{user?.username || '—'}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">{t('settings.role')}</label>
            <span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-sm font-medium text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
              {t(roleInfo.nameKey)}
            </span>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.about')}</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-rose-500">
              <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2c1.5 2 4 2.5 4 5a4 4 0 11-8 0c0-2.5 2.5-3 4-5z" opacity=".9" />
                <path d="M12 11c0 4 3 5 6 5-1 3-4 4-6 4s-5-1-6-4c3 0 6-1 6-5z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('app.name')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Version 1.0.0</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('settings.aboutText')}</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
